import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { X, LogOut, AlertCircle } from 'lucide-react';
import metamaskImg from "../images/metamask.webp";
import trustwalletImg from "../images/trust.png";
import binancewalletImg from "../images/binance.png";
import safepalImg from "../images/safepal.png";
import tokenpocketImg from "../images/tp.png";

// Inline CSS styles (keeping your original styles)
const styles = {
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '1rem'
  },
  modalContent: {
    backgroundColor: '#1F2937',
    borderRadius: '1rem',
    padding: '1.5rem',
    maxWidth: '28rem',
    width: '100%',
    margin: '0 auto',
    position: 'relative'
  },
  closeButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    color: '#9CA3AF',
    cursor: 'pointer'
  },
  closeButtonHover: {
    color: '#FFFFFF'
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: '1.5rem'
  },
  errorContainer: {
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: 'rgba(127, 29, 29, 0.5)',
    border: '1px solid #EF4444',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'flex-start'
  },
  errorIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#EF4444',
    marginRight: '0.5rem',
    flexShrink: 0,
    marginTop: '0.125rem'
  },
  errorText: {
    fontSize: '0.875rem',
    color: '#FECACA'
  },
  accountContainer: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: 'rgba(55, 65, 81, 0.5)',
    borderRadius: '0.5rem'
  },
  accountLabel: {
    fontSize: '0.875rem',
    color: '#9CA3AF',
    marginBottom: '0.25rem'
  },
  accountAddress: {
    fontFamily: 'monospace',
    color: '#FFFFFF',
    wordBreak: 'break-all'
  },
  disconnectButton: {
    width: '100%',
    padding: '0.75rem 0',
    backgroundColor: '#DC2626',
    borderRadius: '0.5rem',
    color: '#FFFFFF',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    outline: 'none'
  },
  disconnectButtonHover: {
    backgroundColor: '#B91C1C'
  },
  walletGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.75rem'
  },
  walletButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#374151',
    borderRadius: '0.5rem',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
    border: 'none',
    outline: 'none'
  },
  walletButtonHover: {
    backgroundColor: '#4B5563'
  },
  walletIcon: {
    width: '2rem',
    height: '2rem',
    marginRight: '0.75rem'
  },
  walletName: {
    fontWeight: '500',
    color: '#FFFFFF'
  },
  connectButton: {
    backgroundColor: '#7C3AED',
    color: '#FFFFFF',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    transition: 'all 0.2s',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    outline: 'none'
  },
  connectButtonHover: {
    backgroundColor: '#6D28D9'
  },
  connectButtonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed'
  }
};

// Define supported wallet types
export const WALLET_TYPES = {
  METAMASK: 'metamask',
  TRUST_WALLET: 'trustwallet',
  BINANCE_WALLET: 'binancewallet',
  TOKEN_POCKET: 'tokenpocket',
  SAFEPAL: 'safepal'
};

const WalletConnector = ({ 
  onConnect, 
  onDisconnect,
  onError,
  onChainChanged,
  requiredChainId = 204, // opBNB chain ID
  buttonClassName = "" // Optional class for custom styling
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);

  // Check for existing connections on component mount
  useEffect(() => {
    checkExistingConnection();
  }, []);

  // Set up event listeners when provider changes
  useEffect(() => {
    if (provider) {
      setupEventListeners();
    }
    return () => {
      removeEventListeners();
    }
  }, [provider]);

  // Check if the user was previously connected
  const checkExistingConnection = async () => {
    try {
      // Check local storage for saved wallet type
      const savedWalletType = localStorage.getItem('walletType');
      
      if (savedWalletType && window.ethereum) {
        // Attempt to reconnect with the saved wallet type
        await connectToWallet(savedWalletType, true);
      } else if (window.ethereum) {
        // Check if already connected to MetaMask
        const ethProvider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await ethProvider.listAccounts();
        
        if (accounts && accounts.length > 0) {
          setProvider(ethProvider);
          setConnectedAccount(accounts[0]);
          setWalletType(WALLET_TYPES.METAMASK);
          
          // FIXED: Correct parameter order - (account, provider, walletType)
          if (onConnect) {
            onConnect(accounts[0], ethProvider, WALLET_TYPES.METAMASK);
          }
        }
      }
    } catch (error) {
      console.error("Error checking existing connection:", error);
    }
  };

  // Set up event listeners for the connected wallet
  const setupEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('disconnect', handleDisconnect);
    }
  };

  // Remove event listeners when component unmounts or wallet disconnects
  const removeEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', handleDisconnect);
    }
  };

  // Handle account changes
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      // User has disconnected all accounts
      handleDisconnect();
    } else if (accounts[0] !== connectedAccount) {
      // User has switched accounts
      setConnectedAccount(accounts[0]);
      // FIXED: Correct parameter order - (account, provider, walletType)
      if (onConnect) {
        onConnect(accounts[0], provider, walletType);
      }
    }
  };

  // Handle chain changes
  const handleChainChanged = (chainId) => {
    // Normalize chainId to decimal
    let normalizedChainId;
    
    if (typeof chainId === 'string') {
      normalizedChainId = parseInt(chainId, 16);
    } else {
      normalizedChainId = chainId;
    }
    
    if (normalizedChainId !== requiredChainId) {
      setError(`Please switch to opBNB (Chain ID: ${requiredChainId})`);
    } else {
      setError(null);
    }
    
    // Reset provider to handle chain change properly
    if (window.ethereum) {
      setProvider(new ethers.providers.Web3Provider(window.ethereum));
    }
    
    // Notify parent component
    if (onChainChanged) {
      onChainChanged(normalizedChainId);
    }
  };

  // Handle wallet disconnection
  const handleDisconnect = () => {
    // Clear state
    setConnectedAccount(null);
    setProvider(null);
    setWalletType(null);
    
    // Remove from local storage
    localStorage.removeItem('walletType');
    
    // Notify parent component
    if (onDisconnect) {
      onDisconnect();
    }
  };

  // Connect to a wallet
  const connectToWallet = async (type, isReconnect = false) => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Check if the wallet is installed
      if (!window.ethereum) {
        if (type === WALLET_TYPES.METAMASK) {
          throw new Error("MetaMask is not installed. Please install MetaMask to continue.");
        } else {
          throw new Error(`${type} is not detected. Please open this site in the ${type} browser.`);
        }
      }
      
      // Request accounts
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts found. Please connect your wallet.");
      }
      
      // Check and switch network if needed
      await switchToRequiredNetwork();
      
      // Create provider
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      
      // Save connection information
      setProvider(web3Provider);
      setConnectedAccount(accounts[0]);
      setWalletType(type);
      
      // Save wallet type to local storage for reconnection
      localStorage.setItem('walletType', type);
      
      // Close modal
      setIsModalOpen(false);
      
      // FIXED: Correct parameter order - (account, provider, walletType)
      if (onConnect) {
        onConnect(accounts[0], web3Provider, type);
      }
    } catch (error) {
      console.error(`Error connecting to ${type}:`, error);
      
      // Format error message
      const errorMessage = error.message || `Failed to connect to ${type}`;
      setError(errorMessage);
      
      // Notify parent component
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Switch to the required network
  const switchToRequiredNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
      // Try to switch to opBNB
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${requiredChainId.toString(16)}` }], // Convert to hex
      });
      return true;
    } catch (switchError) {
      // Network doesn't exist in wallet
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${requiredChainId.toString(16)}`, // Convert to hex
                chainName: 'opBNB',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18
                },
                rpcUrls: ['https://opbnb-mainnet-rpc.bnbchain.org/'],
                blockExplorerUrls: ['https://opbnbscan.com']
              },
            ],
          });
          return true;
        } catch (addError) {
          console.error('Error adding opBNB network', addError);
          throw addError;
        }
      }
      console.error('Error switching network', switchError);
      throw switchError;
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    // Call general disconnect handler
    handleDisconnect();
  };

  // Render wallet connection button or connected account button
  const renderConnectButton = () => {
    // Determine which style to use
    const buttonStyle = {
      ...styles.connectButton,
      ...(hoveredElement === 'connectButton' ? styles.connectButtonHover : {}),
      ...(isConnecting ? styles.connectButtonDisabled : {})
    };

    const finalStyle = buttonClassName ? {} : buttonStyle;

    if (connectedAccount) {
      return (
        <button
          style={finalStyle}
          className={buttonClassName}
          onClick={() => setIsModalOpen(true)}
          onMouseEnter={() => setHoveredElement('connectButton')}
          onMouseLeave={() => setHoveredElement(null)}
        >
          {`${connectedAccount.slice(0, 6)}...${connectedAccount.slice(-4)}`}
        </button>
      );
    } else {
      return (
        <button
          style={finalStyle}
          className={buttonClassName}
          onClick={() => setIsModalOpen(true)}
          disabled={isConnecting}
          onMouseEnter={() => setHoveredElement('connectButton')}
          onMouseLeave={() => setHoveredElement(null)}
        >
          {isConnecting ? 'Connecting...' : 'Connect Wallet'}
        </button>
      );
    }
  };

  // Render wallet selection modal
  const renderWalletModal = () => {
    if (!isModalOpen) return null;
    
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <button
            style={{
              ...styles.closeButton,
              ...(hoveredElement === 'closeButton' ? styles.closeButtonHover : {})
            }}
            onClick={() => setIsModalOpen(false)}
            onMouseEnter={() => setHoveredElement('closeButton')}
            onMouseLeave={() => setHoveredElement(null)}
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          
          <h2 style={styles.title}>
            {connectedAccount ? 'Wallet Connected' : 'Connect Wallet'}
          </h2>
          
          {error && (
            <div style={styles.errorContainer}>
              <AlertCircle style={styles.errorIcon} />
              <p style={styles.errorText}>{error}</p>
            </div>
          )}
          
          {connectedAccount ? (
            <div>
              <div style={styles.accountContainer}>
                <p style={styles.accountLabel}>Connected Account</p>
                <p style={styles.accountAddress}>{connectedAccount}</p>
              </div>
              
              <button
                style={{
                  ...styles.disconnectButton,
                  ...(hoveredElement === 'disconnectButton' ? styles.disconnectButtonHover : {})
                }}
                onClick={disconnectWallet}
                onMouseEnter={() => setHoveredElement('disconnectButton')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <LogOut style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                Disconnect Wallet
              </button>
            </div>
          ) : (
            <div style={styles.walletGrid}>
              {/* MetaMask */}
              <button
                style={{
                  ...styles.walletButton,
                  ...(hoveredElement === 'metamask' ? styles.walletButtonHover : {})
                }}
                onClick={() => connectToWallet(WALLET_TYPES.METAMASK)}
                disabled={isConnecting}
                onMouseEnter={() => setHoveredElement('metamask')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <img 
                  src={metamaskImg} 
                  alt="MetaMask" 
                  style={styles.walletIcon}
                />
                <span style={styles.walletName}>MetaMask</span>
              </button>
              
              {/* Trust Wallet */}
              <button
                style={{
                  ...styles.walletButton,
                  ...(hoveredElement === 'trustwallet' ? styles.walletButtonHover : {})
                }}
                onClick={() => connectToWallet(WALLET_TYPES.TRUST_WALLET)}
                disabled={isConnecting}
                onMouseEnter={() => setHoveredElement('trustwallet')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <img 
                  src={trustwalletImg}
                  alt="Trust Wallet" 
                  style={styles.walletIcon}
                />
                <span style={styles.walletName}>Trust Wallet</span>
              </button>
              
              {/* Binance Wallet */}
              <button
                style={{
                  ...styles.walletButton,
                  ...(hoveredElement === 'binancewallet' ? styles.walletButtonHover : {})
                }}
                onClick={() => connectToWallet(WALLET_TYPES.BINANCE_WALLET)}
                disabled={isConnecting}
                onMouseEnter={() => setHoveredElement('binancewallet')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <img 
                  src={binancewalletImg}
                  alt="Binance Wallet" 
                  style={styles.walletIcon}
                />
                <span style={styles.walletName}>Binance Wallet</span>
              </button>
              
              {/* SafePal */}
              <button
                style={{
                  ...styles.walletButton,
                  ...(hoveredElement === 'safepal' ? styles.walletButtonHover : {})
                }}
                onClick={() => connectToWallet(WALLET_TYPES.SAFEPAL)}
                disabled={isConnecting}
                onMouseEnter={() => setHoveredElement('safepal')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <img 
                  src={safepalImg}
                  alt="SafePal" 
                  style={styles.walletIcon}
                />
                <span style={styles.walletName}>SafePal</span>
              </button>
              
              {/* Token Pocket */}
              <button
                style={{
                  ...styles.walletButton,
                  ...(hoveredElement === 'tokenpocket' ? styles.walletButtonHover : {})
                }}
                onClick={() => connectToWallet(WALLET_TYPES.TOKEN_POCKET)}
                disabled={isConnecting}
                onMouseEnter={() => setHoveredElement('tokenpocket')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <img 
                  src={tokenpocketImg} 
                  alt="Token Pocket" 
                  style={styles.walletIcon}
                />
                <span style={styles.walletName}>Token Pocket</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderConnectButton()}
      {renderWalletModal()}
    </>
  );
};

export default WalletConnector;